import { useValueTick } from "../skin";
import TextSpaceBetween from "@components/layout/TextSpaceBetween";
import api from "@services/axios/apiManager";
import { colors } from "@styles/colors";
import { buildXContextHeader } from "@utils/api/headers";
import { formatGasTitle } from "@utils/func/gasTitle";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { useEffect, useState } from "react";
import { Spinner, Text, XStack, YStack } from "tamagui";
import { RiCoupon3Line, RiCoinLine } from "react-icons/ri";
import { buildGasCouponDiscountMap, discountedLineTotal } from "../utils/couponDiscount";
import { useCartPointsRedeem } from "../utils/useCartPointsRedeem";
import { pointsToNtd } from "@utils/points";

interface PreviewItem {
  index: number;
  is_available: boolean;
  current_unit_price: number;
  price_changed: number | null;
}

interface PreviewGasItem extends PreviewItem {
  /** 每瓶樓層費單價（已 honor `floor_fee_setting.elevator_exempt`） */
  floor_fee_unit_price: number;
}

interface PreviewData {
  subtotal: number;
  fee: number;
  /** 樓層費總額 = SUM(gas_items[].floor_fee_unit_price × qty)；0 = 該訂單免收 */
  floor_fee: number;
  /** 平台服務費（flat per order）；0 = apply_app=false / supplier 關閉 / 平台預設 0 */
  platform_service_fee: number;
  coupon_discount: number;
  total: number;
  commodity_items: PreviewItem[];
  gas_items: PreviewGasItem[];
  delivery_slot_available: boolean;
  requires_auth: boolean;
}

const GasOrderSummary = ({ couponResolved = true }: { couponResolved?: boolean }) => {
  const { draftOrderId, orderState, setOrderState, productList, deliveryTime, customerCouponId, setCouponDiscount, setPreviewTotal } = useOrder();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // 等自動帶券決策完成才算費，避免 preview 先用「無券」算出原價、auto-select 後才折扣（閃動）
    if (!couponResolved) return;

    const fetchPreview = async () => {
      if (!draftOrderId || !cusJwtToken) return;
      setLoading(true);
      try {
        const body = {
          customer_coupon_id: customerCouponId || null,
        };
        const res = await api.post(`/app-api/platform-order-drafts/${draftOrderId}/preview`, body, {
          headers: {
            "X-Context": buildXContextHeader(chooseIdentity),
          },
        });
        if (res?.data?.data) {
          setPreviewData(res.data.data);
          // 同步折扣到 store，讓「訂購項目」清單能折在瓦斯價上（與本摘要一致）
          setCouponDiscount(Number(res.data.data.coupon_discount) || 0);
          // 同步券後應付金額，讓「點數折抵」區塊算得出可折上限（與本摘要同一個基準）
          setPreviewTotal(Number(res.data.data.total) || 0);
        }
      } catch (error) {
        console.error("Fetch preview error:", error);
      } finally {
        setLoading(false);
        // 如果目前是 loading 狀態，完成後設回 success 讓下一次更新能被觸發
        if (orderState === "loading") {
          setOrderState("success");
        }
      }
    };

    // 使用 debounce 避免頻繁呼叫 API（150ms：足以 coalesce quantity 連按、又不會讓 coupon 選擇等太久）
    timer = setTimeout(() => {
      fetchPreview();
    }, 150);

    return () => clearTimeout(timer);
  }, [
    draftOrderId,
    orderState,
    JSON.stringify(productList),
    JSON.stringify(deliveryTime),
    customerCouponId,
    cusJwtToken,
    chooseIdentity?.context_id,
    couponResolved,
  ]);

  // 點數折抵：與購物車的「點數折抵」區塊共用同一支計算，兩邊數字必定一致。
  // 與底下的 useValueTick 一樣，必須在提早 return 之前呼叫。
  const { result: points } = useCartPointsRedeem();
  const storedGasNtd = pointsToNtd(points.storedGas);
  const accountNtd = pointsToNtd(points.account);
  const pointsDiscount = storedGasNtd + accountNtd;

  // 金額變動的跳動提示。必須在所有提早 return 之前呼叫——底下有兩個
  // （載入中 spinner、previewData 為空），放在它們後面會讓這個 hook 有時跑有時不跑，
  // React 就會丟 "Rendered more hooks than during the previous render"。
  // 追的是「折後應付金額」：只改點數折抵時 preview.total 不變，追 total 就不會跳。
  const totalTick = useValueTick(Math.max(0, (previewData?.total ?? 0) - pointsDiscount));

  // 載入中、或仍在等自動帶券決策 → 顯示 spinner，避免先閃原價
  if ((loading || !couponResolved) && !previewData) {
    return (
      <YStack ai="center" jc="center" p="$4">
        <Spinner size="small" color={colors.primary} />
      </YStack>
    );
  }

  if (!previewData) return null;

  const { subtotal = 0, fee = 0, floor_fee = 0, coupon_discount = 0, total = 0 } = previewData;

  // 點數在券之後折：preview 的 total 已含券折，這裡再扣掉點數得到真正的應付金額。
  const payable = Math.max(0, total - pointsDiscount);

  // 優惠券折扣「直接扣在瓦斯品項上」：按各瓦斯品項金額比例攤折扣，最後一筆瓦斯吸收四捨五入餘數；
  // 不再單獨顯示「優惠券折扣」這一行。共用 util，與「訂購項目」清單一致。
  const itemDiscount = buildGasCouponDiscountMap(productList, coupon_discount);

  // 訂單摘要顯示時依公斤數排序（小→大）；非瓦斯品項排在最後。折扣以 line_id 對應，排序不影響金額。
  const sortedList = [...productList].sort((a, b) => (a.kilogram ?? Infinity) - (b.kilogram ?? Infinity));

  return (
    <>
      <YStack gap={8} mt={10}>
        {/* 品項明細：公斤數 × 數量；有優惠券時顯示原價（刪除線）+ 折後價 */}
        {productList.length > 0 && (
          <YStack gap={6}>
            {sortedList.map((item, i) => {
              const name = item.gas_type ? formatGasTitle(item.gas_type, item.kilogram) : item.display_name || "商品";
              const original = item.unit_price * item.qty;
              const shown = discountedLineTotal(item, i, itemDiscount);
              const hasDiscount = shown < original;
              return (
                <XStack key={item.line_id ?? i} ai="center" jc="space-between" gap={10}>
                  <Text fontSize={14} color={colors.text} flexShrink={1}>
                    {name} × {item.qty}
                  </Text>
                  <XStack ai="center" gap={7} flexShrink={0}>
                    {hasDiscount && (
                      <Text fontSize={13} color={colors.gray} style={{ textDecorationLine: "line-through" }}>
                        NT$ {original.toLocaleString()}
                      </Text>
                    )}
                    <Text fontSize={15} fontWeight="bold" color={colors.text}>
                      NT$ {shown.toLocaleString()}
                    </Text>
                  </XStack>
                </XStack>
              );
            })}
            <hr />
          </YStack>
        )}
        <YStack gap={4}>
          <TextSpaceBetween title={`小計`} price={`NT$ ${(subtotal - coupon_discount).toLocaleString()}`} />
          {fee > 0 && <TextSpaceBetween title="費用" price={`NT$ ${fee.toLocaleString()}`} />}
          {floor_fee > 0 && <TextSpaceBetween title="樓層費" price={`NT$ ${floor_fee.toLocaleString()}`} />}
          {storedGasNtd > 0 && <TextSpaceBetween title={`存氣點數折抵（${points.storedGas.toLocaleString()} 點）`} price={`−NT$ ${storedGasNtd.toLocaleString()}`} />}
          {accountNtd > 0 && <TextSpaceBetween title={`帳戶點數折抵（${points.account.toLocaleString()} 點）`} price={`−NT$ ${accountNtd.toLocaleString()}`} />}
        </YStack>
        <hr />
        <YStack gap={4}>
          <XStack alignItems="flex-start" justifyContent="space-between" mb={4}>
            <Text fontSize={16} fontWeight="800">
              應付金額
            </Text>
            <YStack ai="flex-end" gap={3}>
              <Text fontSize={22} color={colors.primary} fontWeight="bold">
                {/* 金額變動時跳一下：加購或改數量時視線在商品那一側，
                    不給提示的話不會發現底下的錢已經變了（見 skin/SkinV2.tsx 的 useValueTick） */}
                <span className={totalTick}>NT$ {payable.toLocaleString()}</span>
              </Text>
              {coupon_discount > 0 && (
                <XStack ai="center" gap={4}>
                  <RiCoupon3Line color={colors.primary} size={13} />
                  <Text fontSize={12} fontWeight="bold" color={colors.primary}>
                    已套用優惠券 −NT$ {coupon_discount.toLocaleString()}
                  </Text>
                </XStack>
              )}
              {pointsDiscount > 0 && (
                <XStack ai="center" gap={4}>
                  <RiCoinLine color={colors.primary} size={13} />
                  <Text fontSize={12} fontWeight="bold" color={colors.primary}>
                    已折抵點數 −NT$ {pointsDiscount.toLocaleString()}
                  </Text>
                </XStack>
              )}
            </YStack>
          </XStack>
        </YStack>
      </YStack>
    </>
  );
};

export default GasOrderSummary;
